import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../../../axiosInstance';
import { toast } from '../../../utils/toast';
import useWorkflowApi from '../../api/workflows/useWorkflowApi';
import { useWorkflow } from '../../../components/Admin/Workflows/Wizard/WorkflowContext';
import { normalizeWorkflowLicenseMode } from '../../../utils/workflowLicenseMode';

export const WIZARD_INITIAL_VALUES = {
  name: '',
  description: '',
  workflowTemplateId: '',
  saveAsTemplate: 0,
  license_renewal: 0,
  versionId: '',
  workflowImage: [],
  linkable_workflow_ids: [],
  parent_workflow_id: null,
  workflow_license_mode: 'new_license',
  payment_provider_id: '',
  payment_enabled: false,
  payment_currency: '',
};

const useWizardData = ({
  versionId,
  draftId,
  values,
  setValues,
  setFieldValue,
  content,
}) => {
  const { t } = useTranslation();
  const { loadWorkflow } = useWorkflow();
  const { getAllWorkflows } = useWorkflowApi();

  const [loading, setLoading] = useState(true);
  const [workflowTemplates, setWorkflowTemplates] = useState([]);
  const [allWorkflows, setAllWorkflows] = useState([]);
  const [preJsonData, setPreJsonData] = useState(null);
  const [paymentProviders, setPaymentProviders] = useState([]);
  const [originalName, setOriginalName] = useState('');
  const [originalPublishedAt, setOriginalPublishedAt] = useState();
  const [publishedAtValue, setPublishedAtValue] = useState(new Date());

  const imageToastShownRef = useRef(false);
  const lastLoadedDataRef = useRef(null);
  const dataLoadToastShownRef = useRef(true);

  useEffect(() => {
    if (content) {
      setLoading(true);
      setValues((prev) => {
        const merged = { ...WIZARD_INITIAL_VALUES, ...prev, ...content };
        return {
          ...merged,
          payment_enabled:
            merged.payment_enabled === undefined ||
            merged.payment_enabled === null
              ? Boolean(merged.payment_provider_id || merged.payment_currency)
              : Boolean(merged.payment_enabled),
        };
      });
      setLoading(false);
    }
  }, [content, setValues]);

  useEffect(() => {
    if (preJsonData) {
      const parsed =
        typeof preJsonData === 'string' ? JSON.parse(preJsonData) : preJsonData;
      if (parsed?.nodes || parsed?.edges) {
        const dataStr = JSON.stringify(parsed);
        if (lastLoadedDataRef.current !== dataStr) {
          lastLoadedDataRef.current = dataStr;
          loadWorkflow(parsed);
        }
      }
    }
  }, [preJsonData, loadWorkflow]);

  useEffect(() => {
    const fetchData = async ({ url, isVersion }) => {
      setLoading(true);
      try {
        const response = await axiosInstance.get(url);
        if (response.data) {
          const workflowData = isVersion ? response.data.data : response.data;
          const workflowImageId = workflowData.workflow_image_id;
          let workflowImageArray = [];

          if (workflowImageId) {
            try {
              const imageResponse = await axiosInstance.get(
                `${process.env.REACT_APP_SERVER_URL}/api/v1/files/admin/${workflowImageId}/preview`,
                {
                  withCredentials: true,
                  responseType: 'blob',
                }
              );

              if (imageResponse.status !== 200 || !imageResponse.data?.size) {
                if (!imageToastShownRef.current) {
                  toast.error(t('workflowWizard.imageNotAvailable'), {
                    position: 'top-center',
                  });
                  imageToastShownRef.current = true;
                }
                throw new Error('Workflow image not available');
              }

              const blob = new Blob([imageResponse.data], {
                type: imageResponse.headers['content-type'] || 'image/jpeg',
              });
              const blobUrl = window.URL.createObjectURL(blob);

              workflowImageArray = [
                {
                  id: workflowImageId,
                  preview: blobUrl,
                  name: `workflow-image-${workflowImageId}`,
                  size: blob.size,
                  type: blob.type,
                },
              ];
            } catch (imageError) {
              console.warn(
                'Could not load existing workflow image:',
                imageError
              );
            }
          }

          setValues((prev) => {
            // prettier-ignore
            const paymentEnabledResolved =
              workflowData.payment_enabled === undefined || workflowData.payment_enabled === null
                ? Boolean(workflowData.payment_provider_id || workflowData.payment_currency)
                : Boolean(workflowData.payment_enabled);

            const resolvedMode = normalizeWorkflowLicenseMode(
              workflowData.workflow_license_mode,
              workflowData.license_renewal
            );

            return {
              ...prev,
              name: workflowData.name,
              description: workflowData.description,
              workflowImage: workflowImageArray,
              ...(isVersion ? { versionId } : {}),
              saveAsTemplate: 0,
              linkable_workflow_ids: workflowData.linkable_workflow_ids || [],
              // Prefer the parent the API already resolved: it carries the name,
              // so the field renders even before the workflow list has loaded
              // and even when that list filters the parent out.
              parent_workflow_id: workflowData.parentWorkflow?.id
                ? {
                    label: workflowData.parentWorkflow.name,
                    value: workflowData.parentWorkflow.id,
                  }
                : (workflowData.parent_workflow_id ?? null),
              workflow_license_mode: resolvedMode,
              license_renewal: resolvedMode === 'license_renewal' ? 1 : 0,
              payment_provider_id: workflowData.payment_provider_id ?? '',
              payment_enabled: paymentEnabledResolved,
              payment_currency: workflowData.payment_currency || '',
            };
          });

          if (isVersion) {
            setPublishedAtValue(new Date(workflowData.publishedAt));
            setOriginalPublishedAt(new Date(workflowData.publishedAt));
            setOriginalName(workflowData.name);
          }

          setPreJsonData(workflowData.stages_json);

          if (dataLoadToastShownRef.current) {
            toast.success(
              isVersion
                ? t('workflowWizard.dataLoadedSuccess')
                : t('workflowWizard.draftLoadedSuccess')
            );
            dataLoadToastShownRef.current = false;
          }
        } else {
          setValues(WIZARD_INITIAL_VALUES);
        }
      } catch (error) {
        console.error('Error fetching workflow data:', error);
        toast.error(t('workflowWizard.fetchFailed'));
      } finally {
        setLoading(false);
      }
    };

    if (versionId) {
      fetchData({
        url: `/api/workflows/version/${versionId}`,
        isVersion: true,
      });
    } else if (draftId) {
      fetchData({
        url: `/api/workflows-drafts/${draftId}`,
        isVersion: false,
      });
    }
  }, [setValues, versionId, draftId, t]);

  useEffect(() => {
    async function fetchTemplates() {
      setLoading(true);
      await axiosInstance.get('/api/workflows-template').then((response) => {
        setWorkflowTemplates(response.data);
        setLoading(false);
      });
    }
    fetchTemplates();
  }, []);

  useEffect(() => {
    async function fetchPaymentProviders() {
      const normalizeCurrencies = (value, code) => {
        if (Array.isArray(value)) {
          return value
            .map((item) => String(item).trim().toUpperCase())
            .filter(Boolean);
        }
        if (typeof value === 'string') {
          try {
            const parsed = JSON.parse(value);
            if (Array.isArray(parsed)) {
              return parsed
                .map((item) => String(item).trim().toUpperCase())
                .filter(Boolean);
            }
          } catch {
            console.error('Error parsing supported currencies:', value);
            return [];
          }
          const cleaned = value
            .replaceAll('[', '')
            .replaceAll(']', '')
            .replaceAll('"', '')
            .replaceAll("'", '');
          const splitValues = cleaned
            .split(',')
            .map((item) => item.trim().toUpperCase())
            .filter(Boolean);
          if (splitValues.length > 0) return splitValues;
        }
        return [];
      };

      try {
        const response = await axiosInstance.get(
          '/api/payments/providers/operational'
        );
        const providers = response.data?.data || [];
        const normalized = Array.isArray(providers)
          ? providers.map((provider) => ({
              ...provider,
              supported_currencies: normalizeCurrencies(
                provider.supported_currencies,
                provider.code
              ),
            }))
          : [];
        setPaymentProviders(normalized);
      } catch (error) {
        console.error('Error fetching payment providers:', error);
      }
    }
    fetchPaymentProviders();
  }, []);

  useEffect(() => {
    async function fetchWorkflows() {
      try {
        const workflows = await getAllWorkflows();
        setAllWorkflows(workflows || []);
      } catch (error) {
        console.error('Error fetching workflows:', error);
      }
    }
    fetchWorkflows();
  }, [getAllWorkflows]);

  useEffect(() => {
    const currentIds = values.linkable_workflow_ids || [];
    if (
      currentIds.length > 0 &&
      allWorkflows.length > 0 &&
      typeof currentIds[0] === 'number'
    ) {
      const formattedLinkableWorkflows = currentIds
        .map((id) => {
          const workflow = allWorkflows.find((wf) => wf.id === id);
          return workflow ? { label: workflow.name, value: workflow.id } : null;
        })
        .filter(Boolean);

      setFieldValue('linkable_workflow_ids', formattedLinkableWorkflows);
    }
  }, [values.linkable_workflow_ids, allWorkflows, setFieldValue]);

  useEffect(() => {
    const parentId = values.parent_workflow_id;
    if (
      allWorkflows.length > 0 &&
      parentId !== null &&
      parentId !== undefined &&
      typeof parentId !== 'object'
    ) {
      const workflow = allWorkflows.find((wf) => wf.id === Number(parentId));
      setFieldValue(
        'parent_workflow_id',
        workflow
          ? { label: workflow.name, value: workflow.id }
          : // The parent is no longer selectable (it became a sub workflow
            // itself, or was removed). Keep the id addressable rather than
            // silently rendering an empty field.
            { label: `#${parentId}`, value: Number(parentId) }
      );
    }
  }, [values.parent_workflow_id, allWorkflows, setFieldValue]);

  const handleTemplateSelection = useCallback(
    (templateId) => {
      if (templateId) {
        const selectedTemplate = workflowTemplates.find(
          (template) => template.id === templateId
        );

        if (selectedTemplate) {
          setValues((prevValues) => ({
            ...prevValues,
            workflowTemplateId: selectedTemplate.id || '',
          }));

          setPreJsonData(selectedTemplate.stages_json);

          toast.success(t('workflowWizard.templateLoadedSuccess'));
        }
      }
    },
    [workflowTemplates, setValues, t]
  );

  return {
    loading,
    workflowTemplates,
    allWorkflows,
    preJsonData,
    setPreJsonData,
    originalName,
    originalPublishedAt,
    publishedAtValue,
    setPublishedAtValue,
    handleTemplateSelection,
    paymentProviders,
  };
};

export default useWizardData;
